import { Inject, Injectable } from '@angular/core';
import { ColumnDataInterface, TableCustomizationService } from './_abstract-table-customization.service';
import { firstValueFrom, Observable, of, combineLatest } from 'rxjs';
import { CrmObjectInterface, FieldSchemaInterface } from '@vendasta/crm';
import { CompanyCustomColumns, CrmFieldService, StandardIds, SystemFieldIds } from '../crm-services/field.service';
import { TranslateService } from '@ngx-translate/core';
import { CrmDependencies, CrmInjectionToken } from '../../tokens-and-interfaces';
import { map, take } from 'rxjs/operators';
import { PAGE_ROUTES } from '../../constants';
import { CustomCell, GalaxyColumnDef } from '@vendasta/galaxy/table';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { ParentCompanyNameComponent } from '../../shared-components/custom-table-cells/parent-company-name.component';
import { LeadScoreCellComponent } from '../../shared-components/custom-table-cells/score/score.component';
import { RestrictedGroupService } from '../restricted-group.service';

const LeadScoreColumnCell = {
  fieldIds: [StandardIds.CompanyLeadScore, SystemFieldIds.CompanyID],
  columnDefinition: {
    id: StandardIds.CompanyLeadScore,
  },
  customCellComponent: LeadScoreCellComponent,
};
@Injectable()
export class CompanyTableCustomizationService extends TableCustomizationService {
  private profileLink$: Observable<string>;

  public static getRowFullAddress(crmObject: CrmObjectInterface, crmFieldService: CrmFieldService): string {
    const addressLineOne = crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressLine1);
    const addressLineTwo = crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressLine2);
    const city = crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressCity);
    const state = crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressState);
    const postalCode = crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressPostalCode);
    const country = crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressCountry);

    const addressLineOneData = crmObject.fields?.find((f) => f.fieldId === addressLineOne);
    const addressLineTwoData = crmObject.fields?.find((f) => f.fieldId === addressLineTwo);
    const cityData = crmObject.fields?.find((f) => f.fieldId === city);
    const stateData = crmObject.fields?.find((f) => f.fieldId === state);
    const postalCodeData = crmObject.fields?.find((f) => f.fieldId === postalCode);
    const countryData = crmObject.fields?.find((f) => f.fieldId === country);

    const fullAddress = [
      addressLineOneData?.stringValue,
      addressLineTwoData?.stringValue,
      cityData?.stringValue,
      stateData?.stringValue,
      countryData?.stringValue,
      postalCodeData?.stringValue,
    ];

    return fullAddress.filter((value) => value !== undefined).join(', ');
  }

  constructor(
    protected override readonly crmFieldService: CrmFieldService,
    private readonly translate: TranslateService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly restrictedGroupService: RestrictedGroupService,
  ) {
    super(crmFieldService);
    this.profileLink$ = this.config.routePrefix$.pipe(
      map((prefix) => {
        const viewRoute = `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`;
        return prefix ? `${prefix}/${viewRoute}` : '';
      }),
    );
  }

  private hideIDColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToHide = [
      SystemFieldIds.CompanyExternalID,
      SystemFieldIds.CompanyID,
      StandardIds.CompanyParentCompanyID,
      this.crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressLine1),
      this.crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressLine2),
      this.crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressCity),
      this.crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressState),
      this.crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressPostalCode),
      this.crmFieldService.getFieldId(StandardIds.CompanyPrimaryAddressCountry),
      this.crmFieldService.getFieldId(StandardIds.CompanyWebsite),
    ];

    for (const identifier of columnIdentifiersToHide) {
      const index = columns.findIndex((c) => c.id === identifier);
      if (index >= 0) {
        columns[index].hidden = true;
      }
    }

    for (const nameCol of columns.filter((c) => c.id === StandardIds.CompanyName)) {
      nameCol.sticky = true;
    }

    return columns;
  }

  customizeColumnsMobile(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToShow = new Map<string, boolean>([
      [this.crmFieldService.getFieldId(StandardIds.CompanyName), true],
      [this.crmFieldService.getFieldId(StandardIds.CompanyWebsite), true],
    ]);

    columns.forEach((column) => {
      column.hidden = !columnIdentifiersToShow.get(column.id);
    });
    return [...columns];
  }

  private addCompanyProfileLink(crmObject: CrmObjectInterface, columnData: ColumnDataInterface): void {
    const companyNameField = this.crmFieldService.getFieldId(StandardIds.CompanyName);
    this.profileLink$.pipe(take(1)).subscribe((profileLink) => {
      profileLink = profileLink.replace(':crmObjectId', crmObject.crmObjectId || '');
      columnData[companyNameField] = {
        cellType: 'text',
        value: this.getRowFullName(crmObject),
        link: profileLink,
      };
    });
  }

  customizeRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void {
    this.customizeNextTaskDueDateCellData(columnData);
    this.addCompanyProfileLink(crmObject, columnData);
    this.addPrimaryCompanyNameRowData(crmObject, schemaMap, columnData);
    this.addFullAddressColumnData(crmObject, schemaMap, columnData);
    this.addCustomCells(this.config.company?.tableCustomCells || [], crmObject, columnData);
  }

  private customizeNextTaskDueDateCellData(columnData: ColumnDataInterface): void {
    const cell = columnData[this.crmFieldService.getFieldId(StandardIds.CompanyNextTaskDueDate)];
    if (cell) {
      cell.cellType = 'timestamp';
    }
  }

  private addFullAddressColumnData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void {
    columnData[CompanyCustomColumns.FullAddress] = {
      cellType: 'text',
      value: this.getRowFullAddress(crmObject),
    };
  }

  customizeColumns(columns: GalaxyColumnDef[], isMobile: boolean): GalaxyColumnDef[] {
    let customizedColumns = this.hideIDColumns(columns);
    customizedColumns = this.addFullAddressColumn(customizedColumns);
    customizedColumns = this.addParentCompanyNameColumn(customizedColumns);
    const customCells = this.config.company?.tableCustomCells || [];
    customCells.push(LeadScoreColumnCell);
    customizedColumns = this.addCustomColumns(
      customCells.map((c) => c.columnDefinition),
      customizedColumns,
    );
    if (isMobile) {
      customizedColumns = this.customizeColumnsMobile(customizedColumns);
    }
    return [...customizedColumns];
  }

  getRowFullName(crmObject: CrmObjectInterface): string {
    const name = this.crmFieldService.getFieldId(StandardIds.CompanyName);
    const nameData = crmObject.fields?.find((f) => f.fieldId === name);
    return nameData?.stringValue || '';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAssociationInfo(crmObject: CrmObjectInterface): string {
    return '';
  }

  getRowFullAddress(crmObject: CrmObjectInterface): string {
    return CompanyTableCustomizationService.getRowFullAddress(crmObject, this.crmFieldService);
  }

  private addFullAddressColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      ...columns,
      {
        id: CompanyCustomColumns.FullAddress,
        title: this.translate.instant('LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.FULL_ADDRESS'),
        sortable: false,
      },
    ];
  }

  private addParentCompanyNameColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: CompanyCustomColumns.ParentCompanyName,
        title: 'Parent company name',
        hidden: true,
      },
      ...columns,
    ];
  }

  private async addPrimaryCompanyNameRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): Promise<void> {
    let parentCompanyID = '';
    const parentCompanyIDFieldValue = this.crmFieldService.getFieldValueFromCrmObject(
      crmObject,
      StandardIds.CompanyParentCompanyID,
    );
    if (parentCompanyIDFieldValue?.stringValue) {
      parentCompanyID = parentCompanyIDFieldValue.stringValue;
    }
    columnData[CompanyCustomColumns.ParentCompanyName] = {
      cellType: 'custom',
      value: parentCompanyID,
      component: ParentCompanyNameComponent,
    } as CustomCell;
  }

  private makeGroupFilterRestricted(
    restrictedToGroupId: string,
    filters: GalaxyFilterDefinitionInterface[],
  ): GalaxyFilterDefinitionInterface[] {
    if (!restrictedToGroupId) {
      return filters;
    }
    const groupIDIndex = filters.findIndex((f) => f.fieldId === SystemFieldIds.CompanyGroupID);
    if (groupIDIndex > -1) {
      filters[groupIDIndex].preset = true;
      filters[groupIDIndex].lockedValue = true;
    }
    return filters;
  }

  customizeFilters(filters: GalaxyFilterDefinitionInterface[]): Observable<GalaxyFilterDefinitionInterface[]> {
    const overrides$ = this.config.company?.filterInputOverrides$ ?? of([]);
    const appliedOverrides$ = overrides$.pipe(map((overrides) => this.overrideCustomFilters(filters, overrides)));
    return combineLatest([this.restrictedGroupService.getCurrentGroupId(), appliedOverrides$]).pipe(
      map(([restrictedMarketId, filters]: [string, GalaxyFilterDefinitionInterface[]]) =>
        this.makeGroupFilterRestricted(restrictedMarketId, filters),
      ),
    );
  }

  async getInitialFilters(): Promise<GalaxyFilterInterface[]> {
    return firstValueFrom(this.restrictedGroupService.getCurrentGroupId()).then((selectedGroupId) => {
      if (!selectedGroupId) {
        return [];
      }
      return [
        {
          fieldId: SystemFieldIds.CompanyGroupID,
          filterId: 'filterCompanyByRestrictedGroupID',
          operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
          values: [
            {
              string: selectedGroupId,
            },
          ],
        },
      ];
    });
  }
}
